import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
	PhoneIncoming,
	PhoneOutgoing,
	PhoneMissed,
	PhoneOff,
	PhoneCall,
	MessageSquare,
	Loader2,
	Search,
	Mic,
	X,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../components/ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "../../components/ui/table";
import { cn } from "../../lib/utils";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";
import { getPublicFileUrl } from "../../config";
import usePermissions from "../../hooks/usePermissions";
import { ligar, useLigacoes } from "../../ligacoes/store";

const STATUS = {
	answered: { rotulo: "Atendida", cor: "text-emerald-600" },
	missed: { rotulo: "Perdida", cor: "text-destructive" },
	rejected: { rotulo: "Recusada", cor: "text-amber-600" },
	failed: { rotulo: "Não atendida", cor: "text-muted-foreground" },
	ringing: { rotulo: "Tocando", cor: "text-emerald-600" },
	connected: { rotulo: "Em conversa", cor: "text-emerald-600" },
};

const CHIPS = [
	{ valor: "", rotulo: "Todas" },
	{ valor: "answered", rotulo: "Atendidas" },
	{ valor: "missed", rotulo: "Perdidas" },
	{ valor: "rejected", rotulo: "Recusadas" },
	{ valor: "failed", rotulo: "Não atendidas" },
];

export const mmss = (s) =>
	`${String(Math.floor((s || 0) / 60)).padStart(2, "0")}:${String((s || 0) % 60).padStart(2, "0")}`;

const hojeISO = () => {
	const d = new Date();
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const IconeTipo = ({ l }) => {
	if (l.status === "missed") return <PhoneMissed className="h-4 w-4 text-destructive" />;
	if (l.status === "rejected") return <PhoneOff className="h-4 w-4 text-amber-600" />;
	if (l.direction === "outbound") return <PhoneOutgoing className="h-4 w-4 text-sky-600" />;
	return <PhoneIncoming className="h-4 w-4 text-emerald-600" />;
};

const Historico = ({ aoResumo }) => {
	const navigate = useNavigate();
	const lig = useLigacoes();
	const { temPermissao } = usePermissions();
	const veTudo = temPermissao("calls:history");

	const [filtros, setFiltros] = useState({
		search: "",
		status: "",
		direction: "",
		whatsappId: "",
		userId: "",
		dateFrom: "",
		dateTo: "",
	});
	const [busca, setBusca] = useState("");
	const [pagina, setPagina] = useState(1);
	const [dados, setDados] = useState({ calls: [], totals: {}, hasMore: false, count: 0 });
	const [carregando, setCarregando] = useState(true);
	const [usuarios, setUsuarios] = useState([]);
	const [conexoes, setConexoes] = useState([]);
	const [ligando, setLigando] = useState(null);

	// Busca digitada espera a pessoa parar de digitar.
	useEffect(() => {
		const t = setTimeout(() => setFiltros((f) => ({ ...f, search: busca })), 400);
		return () => clearTimeout(t);
	}, [busca]);

	const carregar = useCallback(
		async (pag = 1) => {
			setCarregando(true);
			try {
				const params = Object.fromEntries(
					Object.entries({ ...filtros, page: pag }).filter(([, v]) => v !== "" && v != null)
				);
				const { data } = await api.get("/calls", { params });
				setDados((atual) =>
					pag === 1 ? data : { ...data, calls: [...atual.calls, ...data.calls] }
				);
				setPagina(pag);
			} catch (err) {
				toastError(err);
			}
			setCarregando(false);
		},
		[filtros]
	);

	useEffect(() => {
		carregar(1);
	}, [carregar]);

	// Resumo do dia para os cartões do topo, sempre com a régua de "hoje".
	const carregarResumo = useCallback(async () => {
		try {
			const hoje = hojeISO();
			const { data } = await api.get("/calls", { params: { dateFrom: hoje, dateTo: hoje } });
			aoResumo?.(data);
		} catch {
			// cartões ficam como estavam
		}
	}, [aoResumo]);

	useEffect(() => {
		carregarResumo();
	}, [carregarResumo]);

	useEffect(() => {
		if (veTudo) {
			api
				.get("/users", { params: { pageNumber: 1 } })
				.then(({ data }) => setUsuarios(data.users || []))
				.catch(() => {});
		}
		api
			.get("/whatsapp/")
			.then(({ data }) => setConexoes((data || []).filter((w) => (w.channel || "whatsapp") === "whatsapp")))
			.catch(() => {});
	}, [veTudo]);

	// Ligação nova ou encerrada atualiza a lista e os cartões sem F5.
	useEffect(() => {
		const socket = openSocket();
		socket.on("ligacao", ({ action }) => {
			if (["incoming", "outgoing", "ended", "recording"].includes(action)) {
				carregar(1);
				carregarResumo();
			}
		});
		return () => socket.disconnect();
	}, [carregar, carregarResumo]);

	const mudar = (campo, valor) => setFiltros((f) => ({ ...f, [campo]: valor }));
	const limpar = () => {
		setBusca("");
		setFiltros({ search: "", status: "", direction: "", whatsappId: "", userId: "", dateFrom: "", dateTo: "" });
	};
	const filtrando = Object.entries(filtros).some(([k, v]) => k !== "status" && v);

	const totalGeral = Object.values(dados.totals || {}).reduce((a, b) => a + b, 0);

	const religar = async (l) => {
		setLigando(l.id);
		try {
			await ligar(l.ticketId ? { ticketId: l.ticketId } : { number: l.number });
		} catch (err) {
			toastError(err);
		}
		setLigando(null);
	};

	return (
		<div className="space-y-3">
			<div className="flex flex-wrap items-center gap-2">
				<div className="relative min-w-[200px] flex-1">
					<Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
					<Input
						className="pl-8"
						placeholder="Buscar por nome ou número"
						value={busca}
						onChange={(e) => setBusca(e.target.value)}
					/>
				</div>
				<Select value={filtros.direction || "todas"} onValueChange={(v) => mudar("direction", v === "todas" ? "" : v)}>
					<SelectTrigger className="w-[150px]">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="todas">Entrada e saída</SelectItem>
						<SelectItem value="inbound">Recebidas</SelectItem>
						<SelectItem value="outbound">Feitas</SelectItem>
					</SelectContent>
				</Select>
				{conexoes.length > 1 && (
					<Select value={filtros.whatsappId || "todas"} onValueChange={(v) => mudar("whatsappId", v === "todas" ? "" : v)}>
						<SelectTrigger className="w-[170px]">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="todas">Todas as conexões</SelectItem>
							{conexoes.map((c) => (
								<SelectItem key={c.id} value={String(c.id)}>
									{c.name}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				)}
				{veTudo && usuarios.length > 0 && (
					<Select value={filtros.userId || "todos"} onValueChange={(v) => mudar("userId", v === "todos" ? "" : v)}>
						<SelectTrigger className="w-[170px]">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="todos">Toda a equipe</SelectItem>
							{usuarios.map((u) => (
								<SelectItem key={u.id} value={String(u.id)}>
									{u.name}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				)}
				<Input
					type="date"
					aria-label="De"
					className="w-[150px]"
					value={filtros.dateFrom}
					onChange={(e) => mudar("dateFrom", e.target.value)}
				/>
				<Input
					type="date"
					aria-label="Até"
					className="w-[150px]"
					value={filtros.dateTo}
					onChange={(e) => mudar("dateTo", e.target.value)}
				/>
				{filtrando && (
					<Button variant="ghost" size="sm" onClick={limpar}>
						<X className="h-4 w-4" />
						Limpar
					</Button>
				)}
			</div>

			<div className="flex flex-wrap gap-1.5">
				{CHIPS.map((c) => {
					const qtd = c.valor ? dados.totals?.[c.valor] || 0 : totalGeral;
					const ativo = filtros.status === c.valor;
					return (
						<button
							key={c.valor || "todas"}
							type="button"
							onClick={() => mudar("status", c.valor)}
							className={cn(
								"rounded-full border px-3 py-1 text-xs font-medium transition-colors",
								ativo ? "border-primary bg-primary/10 text-primary" : "text-muted-foreground hover:bg-accent"
							)}
						>
							{c.rotulo} <span className="tabular-nums opacity-70">{qtd}</span>
						</button>
					);
				})}
			</div>

			{!veTudo && (
				<p className="text-xs text-muted-foreground">
					Mostrando só as ligações que você fez ou atendeu.
				</p>
			)}

			<div className="rounded-xl border bg-card">
				{carregando && dados.calls.length === 0 ? (
					<div className="flex justify-center py-16">
						<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
					</div>
				) : dados.calls.length === 0 ? (
					<div className="py-16 text-center">
						<PhoneCall className="mx-auto h-8 w-8 text-muted-foreground/50" />
						<p className="mt-3 text-sm font-medium">
							{filtrando || filtros.status ? "Nenhuma ligação com esses filtros" : "Nenhuma ligação ainda"}
						</p>
						<p className="mt-1 text-xs text-muted-foreground">
							{filtrando || filtros.status
								? "Mude o período ou limpe os filtros."
								: lig.disponivel
									? "Ligue pelo telefone flutuante ou pelo botão verde num atendimento."
									: "Pareie o aparelho de ligações na aba Aparelhos para começar."}
						</p>
					</div>
				) : (
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead className="w-8" />
								<TableHead>Contato</TableHead>
								<TableHead className="hidden md:table-cell">Conexão</TableHead>
								<TableHead className="hidden sm:table-cell">Atendente</TableHead>
								<TableHead>Quando</TableHead>
								<TableHead className="text-right">Duração</TableHead>
								<TableHead className="hidden lg:table-cell">Gravação</TableHead>
								<TableHead className="w-20" />
							</TableRow>
						</TableHeader>
						<TableBody>
							{dados.calls.map((l) => {
								const st = STATUS[l.status] || STATUS.failed;
								return (
									<TableRow key={l.id}>
										<TableCell>
											<IconeTipo l={l} />
										</TableCell>
										<TableCell>
											<p className="font-medium">{l.contact?.name || l.number || "—"}</p>
											<p className="text-xs text-muted-foreground">
												{l.number && l.contact?.name !== l.number ? `${l.number} · ` : ""}
												<span className={st.cor}>{st.rotulo}</span>
											</p>
										</TableCell>
										<TableCell className="hidden text-sm text-muted-foreground md:table-cell">
											{l.whatsapp?.name || "—"}
										</TableCell>
										<TableCell className="hidden text-sm sm:table-cell">
											{l.user?.name || <span className="text-muted-foreground">—</span>}
										</TableCell>
										<TableCell className="whitespace-nowrap text-sm">
											{new Date(l.startedAt).toLocaleString("pt-BR", {
												day: "2-digit",
												month: "2-digit",
												hour: "2-digit",
												minute: "2-digit",
											})}
										</TableCell>
										<TableCell className="text-right text-sm tabular-nums">
											{l.status === "answered" ? mmss(l.duration) : "—"}
										</TableCell>
										<TableCell className="hidden lg:table-cell">
											{l.recordingUrl ? (
												<audio controls preload="none" className="h-8 w-56" src={getPublicFileUrl(l.recordingUrl)} />
											) : (
												<span className="flex items-center gap-1 text-xs text-muted-foreground">
													<Mic className="h-3 w-3" /> —
												</span>
											)}
										</TableCell>
										<TableCell>
											<div className="flex justify-end gap-0.5">
												<Button
													variant="ghost"
													size="icon"
													className="h-8 w-8 text-emerald-600"
													title={`Ligar de volta${l.number ? ` para ${l.number}` : ""}`}
													aria-label="Ligar de volta"
													disabled={!lig.disponivel || !!lig.minha || ligando === l.id || (!l.number && !l.ticketId)}
													onClick={() => religar(l)}
												>
													{ligando === l.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <PhoneCall className="h-4 w-4" />}
												</Button>
												{l.ticketId && (
													<Button
														variant="ghost"
														size="icon"
														className="h-8 w-8"
														title="Abrir conversa"
														aria-label="Abrir conversa"
														onClick={() => navigate(`/tickets/${l.ticketId}`)}
													>
														<MessageSquare className="h-4 w-4" />
													</Button>
												)}
											</div>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				)}
			</div>

			{dados.hasMore && (
				<div className="flex justify-center">
					<Button variant="outline" disabled={carregando} onClick={() => carregar(pagina + 1)}>
						{carregando && <Loader2 className="h-4 w-4 animate-spin" />}
						Carregar mais ({dados.count - dados.calls.length} restantes)
					</Button>
				</div>
			)}
		</div>
	);
};

export default Historico;
